import TawkMessengerReact from '@tawk.to/tawk-messenger-react';
import { useUserData } from 'components/hooks';
import sha256 from 'crypto-js/sha256';
import React, {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { TawkMessagerRef } from 'tawk-messenger-react';

interface TawkContextValues {
  openChat: () => void;
}

const TawkContext = createContext<TawkContextValues>({
  openChat: () => undefined,
});

const TawkContextProvider: FC<PropsWithChildren<{ hideOnRoutes?: string[] }>> = ({
  children,
  hideOnRoutes = [' /login', '/register'],
}) => {
  const [ready, setReady] = useState(false);

  const { user } = useUserData();
  const { pathname } = useLocation();
  const tawkMessengerRef = useRef<TawkMessagerRef>(null);

  const hidden = useMemo(() => {
    return hideOnRoutes.some(route => pathname.includes(route));
  }, [hideOnRoutes, pathname]);

  useEffect(() => {
    if (ready && user && tawkMessengerRef.current) {
      try {
        tawkMessengerRef.current.setAttributes({
          name: user.fullName,
          email: user.email,
          // @ts-ignore
          hash: sha256(user.email || '', process.env.REACT_APP_TAWK_API_KEY),
        });
      } catch (error) {
        console.error('Error setting Tawk attributes:', error);
      }
    }
  }, [user, tawkMessengerRef, ready]);

  const openChat = () => {
    if (tawkMessengerRef.current) {
      tawkMessengerRef.current.maximize();
    }
  };

  const state = {
    openChat,
  };

  return (
    <TawkContext.Provider value={state}>
      {process.env.NODE_ENV === 'production' && !hidden && user && (
        <TawkMessengerReact
          ref={tawkMessengerRef}
          propertyId="6010472cc31c9117cb72aca7"
          widgetId="1esvpc1ma"
          onLoad={() => setReady(true)}
        />
      )}
      {children}
    </TawkContext.Provider>
  );
};

const useTawkChat = () => useContext(TawkContext);

export { TawkContextProvider, useTawkChat };

export default TawkContext;
